import {
  getEncounterTimelineData,
  getTimelineSpellNameById,
} from 'app/frontend/base/services/raidPlanData'
import { difficultiesLabels } from 'app/frontend/types/custom/Difficulty'
import {
  getAssignmentScopeIndexes,
  getEffectiveAbilityCustomGrouping,
  getEffectiveAssignmentGroups,
  getEffectiveAssignmentViewMode,
  getSoakPrefix,
  normalizeAssignmentBox,
  normalizeAssignmentCast,
} from '../../TeamPlans/components/assignmentTimeline'
import { resolvePhaseTiming } from '../../TeamPlans/utils/phaseTiming'
import { EventActivityLinkedPlanData } from '../hooks/useEventActivityLinkedPlanData'
import { EventActivityParticipantEntry } from './eventActivityParticipantEntry'
import {
  ResolvedAssignmentEntity,
  ResolvedAssignmentParticipant,
} from './eventActivityTimelineResolver'
import { resolveEventParticipantCharacter } from './eventActivityParticipants'
import { unavailableParticipantStatuses } from 'app/frontend/types/custom/EventParticipantStatus'

type TimedExportLine = {
  at: number
  line: string
}

type AbilitySummary = {
  label: string
  slotOrder: string[]
  tokensBySlot: Map<string, string[]>
}

const clamp = (value: number, min: number, max: number) =>
  Math.max(min, Math.min(max, value))

const normalizeRealmChatName = (realmName: string) =>
  realmName.replaceAll(' ', '').replaceAll('-', '')

const formatTimeValue = (value: number) =>
  Math.max(Math.floor(value), 0).toString()

const normalizeRoleToken = (role: string) => {
  if (role === 'heal') return 'healer'
  return role
}

const toEntityToken = (entity: ResolvedAssignmentEntity) => {
  if (entity.kind === 'participant') return entity.participant.name
  if (entity.sourceEntity.kind === 'everyone') return 'everyone'
  if (entity.sourceEntity.kind === 'group')
    return `group${entity.sourceEntity.group}`
  if (entity.sourceEntity.kind === 'role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'collection') {
    if (entity.sourceEntity.collection === 'everyone') return 'everyone'
    if (entity.sourceEntity.collection === 'healers') return 'healer'
    if (entity.sourceEntity.collection === 'tanks') return 'tank'
    return entity.sourceEntity.collection
  }
  if (entity.sourceEntity.kind === 'by_role') {
    return normalizeRoleToken(entity.sourceEntity.role)
  }
  if (entity.sourceEntity.kind === 'member') return entity.sourceEntity.label
  return ''
}

const computePhaseBoundaries = (maxTime: number, phaseDurations: number[]) => {
  return phaseDurations.reduce<number[]>(
    (currentBoundaries, defaultBoundary, index) => {
      const prevCurrentBoundary =
        index === 0 ? 0 : currentBoundaries[index - 1] ?? 0
      const rawBoundary = prevCurrentBoundary + defaultBoundary
      currentBoundaries.push(clamp(rawBoundary, prevCurrentBoundary, maxTime))
      return currentBoundaries
    },
    [],
  )
}

const getPhaseAt = (time: number, phaseBoundaries: number[]) => {
  for (
    let phaseIndex = 0;
    phaseIndex < phaseBoundaries.length;
    phaseIndex += 1
  ) {
    if (time < phaseBoundaries[phaseIndex]) {
      return phaseIndex
    }
  }
  return Math.max(phaseBoundaries.length - 1, 0)
}

const getPhaseStart = (phaseIndex: number, phaseBoundaries: number[]) => {
  if (phaseIndex <= 0) return 0
  return phaseBoundaries[phaseIndex - 1] ?? 0
}

const getBossTimelineEntries = ({
  maxTime,
  phaseBoundaries,
  phaseDurations,
  abilities,
}: {
  maxTime: number
  phaseBoundaries: number[]
  phaseDurations: number[]
  abilities: Record<
    string,
    {
      spell_id: number
      entries: Array<{ time: number; duration: number; phaseIndex: number }>
    }
  >
}) => {
  return Object.values(abilities).flatMap((ability) =>
    ability.entries.flatMap((entry) => {
      const phaseStart = getPhaseStart(entry.phaseIndex, phaseBoundaries)
      const phaseEnd = phaseBoundaries[entry.phaseIndex] ?? maxTime
      const phaseDuration = phaseDurations[entry.phaseIndex]
      if (phaseDuration === undefined) return []
      if (entry.time > phaseDuration || entry.time < 0) return []
      const entryTime = Math.floor(phaseStart + entry.time)
      if (entryTime < phaseStart || entryTime >= phaseEnd) return []
      return [
        {
          spellId: ability.spell_id,
          time: entryTime,
        },
      ]
    }),
  )
}

const getNearestBossSpellId = (
  castAt: number,
  bossEntries: Array<{ spellId: number; time: number }>,
) => {
  const candidates = bossEntries
    .map((entry) => ({
      spellId: entry.spellId,
      delta: Math.abs(entry.time - castAt),
      time: entry.time,
    }))
    .filter((entry) => entry.delta <= 3)
    .sort((left, right) => {
      if (left.delta !== right.delta) return left.delta - right.delta
      if (left.time !== right.time) return left.time - right.time
      return left.spellId - right.spellId
    })
  return candidates[0]?.spellId
}

const toAssignmentEntityTokens = (entities: ResolvedAssignmentEntity[]) =>
  entities
    .filter((entity) => !entity.issue)
    .map(toEntityToken)
    .filter((token) => token.length > 0)

const toCastTargetSuffix = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  if (!resolvedTargets.length) return ''
  const tokens = toAssignmentEntityTokens(resolvedTargets)
  if (!tokens.length) return ''
  return ` on ${tokens.join(', ')}`
}

const toCastTargetCharacterNames = (
  castId: string,
  resolvedByCastId: Record<string, ResolvedAssignmentEntity[]>,
) => {
  const resolvedTargets = resolvedByCastId[castId] ?? []
  const seenNames = new Set<string>()
  return resolvedTargets.reduce<string[]>((acc, entity) => {
    if (entity.kind !== 'participant' || entity.issue) return acc
    const name = entity.participant.name.trim()
    if (!name.length || seenNames.has(name)) return acc
    seenNames.add(name)
    acc.push(name)
    return acc
  }, [])
}

const appendTextProperty = (parts: string[], text?: string) => {
  const trimmed = text?.trim()
  if (trimmed) {
    parts.push(`text:${trimmed}`)
  }
}

const appendTtsProperty = (parts: string[], ttsMessage?: string) => {
  const trimmed = ttsMessage?.trim()
  if (trimmed) {
    parts.push(`TTS:${trimmed}`)
  }
}

const parseRgbString = (value?: string) => {
  const trimmed = value?.trim()
  if (!trimmed) return null
  const rgbMatch = trimmed.match(
    /^rgb\(\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*,\s*([+-]?\d*\.?\d+)\s*\)$/i,
  )
  if (!rgbMatch) return null
  const red = Number(rgbMatch[1])
  const green = Number(rgbMatch[2])
  const blue = Number(rgbMatch[3])
  if (
    !Number.isFinite(red) ||
    !Number.isFinite(green) ||
    !Number.isFinite(blue)
  ) {
    return null
  }
  const normalize = (channel: number, max: number) =>
    Number((Math.max(0, Math.min(channel, max)) / max).toFixed(4))
  return {
    red: normalize(red, 255),
    green: normalize(green, 255),
    blue: normalize(blue, 255),
    alpha: 1,
  }
}

const appendColorsProperty = (parts: string[], rgb?: string) => {
  const parsed = parseRgbString(rgb)
  if (!parsed) return
  parts.push(
    `colors:${parsed.red} ${parsed.green} ${parsed.blue} ${parsed.alpha}`,
  )
}

const appendGlowunitProperty = (parts: string[], characterNames: string[]) => {
  if (!characterNames.length) return
  parts.push(`glowunit:${characterNames.join(' ')}`)
}

const buildInviteList = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const selectedParticipantIds = new Set(
    (selectedParticipants ?? []).map(
      (participant) => participant.participantId,
    ),
  )
  const fallbackEntries = allEntries.filter((entry) => {
    if (unavailableParticipantStatuses.includes(entry.participant.status))
      return false
    return entry.selections[activity.id]?.selected === true
  })
  const baseEntries =
    selectedParticipantIds.size > 0
      ? allEntries.filter((entry) =>
          selectedParticipantIds.has(entry.participant.id),
        )
      : fallbackEntries

  return baseEntries
    .map((entry) => resolveEventParticipantCharacter(entry, activity.id))
    .map((character) => ({
      id: character.id,
      name: character.name,
      value: `${character.name}-${normalizeRealmChatName(
        character.realm.name,
      )}`,
    }))
    .reduce<Array<{ id: number; name: string; value: string }>>((acc, item) => {
      if (acc.some((entry) => entry.id === item.id)) return acc
      acc.push(item)
      return acc
    }, [])
    .sort((left, right) => left.name.localeCompare(right.name))
    .map((item) => item.value)
}

export const buildEventActivityInviteListLine = ({
  activity,
  allEntries,
  selectedParticipants,
}: {
  activity: wowaudit.EventActivity
  allEntries: EventActivityParticipantEntry[]
  selectedParticipants?: ResolvedAssignmentParticipant[]
}) => {
  const inviteList = buildInviteList({
    activity,
    allEntries,
    selectedParticipants,
  })
  return `invitelist:${inviteList.join(' ')};`
}

export const buildEventActivityLinkedPlanNote = ({
  activity,
  linkedPlanData,
  allEntries,
}: {
  activity: wowaudit.EventActivity
  linkedPlanData?: EventActivityLinkedPlanData
  allEntries: EventActivityParticipantEntry[]
}) => {
  const encounter = activity.encounter
  const linkedRaidPlan = linkedPlanData?.linkedRaidPlan
  const selectedParticipants = linkedPlanData?.selectedParticipants
  const difficultyLabel =
    difficultiesLabels[activity.difficulty] ?? activity.difficulty
  const header = encounter
    ? `EncounterID:${encounter.wclId};Difficulty:${difficultyLabel};Name:${encounter.name}`
    : ''

  const noteChunks: string[] = []
  if (header) {
    noteChunks.push(header)
  }

  let timedLines: string[] = []
  let summaryLines: string[] = []
  if (linkedPlanData && linkedRaidPlan) {
    const encounterTimeline =
      getEncounterTimelineData()[linkedRaidPlan.encounterId.toString()]
    const encounterDifficulty =
      encounterTimeline?.difficulties.find(
        (difficulty) => difficulty.difficulty === linkedRaidPlan.difficulty,
      ) ?? encounterTimeline?.difficulties[0]
    if (encounterDifficulty) {
      const roundUpToThirtySeconds = (value: number) =>
        Math.ceil(value / 30) * 30
      const encounterMax = encounterDifficulty.maxTime ?? 0
      const maxTime =
        encounterMax > 0
          ? roundUpToThirtySeconds(Math.max(60, encounterMax))
          : roundUpToThirtySeconds(
              Math.max(
                60,
                encounterDifficulty.phases.reduce(
                  (sum, phase) => sum + phase.duration,
                  0,
                ),
                linkedRaidPlan.timeline.actors.reduce((max, actor) => {
                  return actor.casts.reduce(
                    (innerMax, cast) => Math.max(innerMax, cast.at),
                    max,
                  )
                }, 0),
                (linkedRaidPlan.timeline.general?.casts ?? []).reduce(
                  (max, cast) => Math.max(max, cast.at),
                  0,
                ),
              ),
            )
      const { effectivePhaseDurations } = resolvePhaseTiming({
        phases: encounterDifficulty.phases,
        persistedPhaseDurations: linkedRaidPlan.timeline.phase_durations,
      })
      const phaseBoundaries = computePhaseBoundaries(
        maxTime,
        effectivePhaseDurations,
      )
      const bossEntries = getBossTimelineEntries({
        maxTime,
        phaseBoundaries,
        phaseDurations: effectivePhaseDurations,
        abilities: encounterDifficulty.abilities,
      })

      const actorCastLines: TimedExportLine[] = linkedPlanData.resolvedActors
        .filter(
          (resolvedActor) =>
            !resolvedActor.issue &&
            resolvedActor.participant &&
            !resolvedActor.isIgnored,
        )
        .flatMap((resolvedActor) => {
          return resolvedActor.actor.casts
            .map((cast) => {
              const phaseIndex = getPhaseAt(cast.at, phaseBoundaries)
              const phaseStart = getPhaseStart(phaseIndex, phaseBoundaries)
              const relativeTime = Math.max(cast.at - phaseStart, 0)
              const parts = [
                `time:${formatTimeValue(relativeTime)}`,
                `ph:${phaseIndex + 1}`,
              ]
              const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
              if (bossSpellId) {
                parts.push(`bossSpell:${bossSpellId}`)
              }
              parts.push(
                `tag:${
                  resolvedActor.participant?.name ?? resolvedActor.actor.label
                }`,
              )
              if (cast.spell_id !== null) {
                parts.push(`spellid:${cast.spell_id}`)
              }
              const fallbackMessage =
                cast.spell_id !== null
                  ? getTimelineSpellNameById()[cast.spell_id] ??
                    `Spell ${cast.spell_id}`
                  : ''
              const shouldAddTargetsToReminderText =
                cast.add_targets_to_reminder_text !== false
              const castMessage = `${cast.message?.trim() || fallbackMessage}${
                shouldAddTargetsToReminderText
                  ? toCastTargetSuffix(
                      cast.id,
                      linkedPlanData.castTargetResolverResult.resolvedByCastId,
                    )
                  : ''
              }`.trim()
              appendTextProperty(parts, castMessage)
              appendColorsProperty(parts, cast.rgb)
              appendTtsProperty(parts, cast.tts_message)
              if (cast.glow_targets_on_raid_frames !== false) {
                appendGlowunitProperty(
                  parts,
                  toCastTargetCharacterNames(
                    cast.id,
                    linkedPlanData.castTargetResolverResult.resolvedByCastId,
                  ),
                )
              }
              if (cast.spell_id === null && !castMessage.length) {
                return null
              }
              return {
                at: cast.at,
                line: `${parts.join(';')};`,
              }
            })
            .filter((entry): entry is TimedExportLine => entry !== null)
        })
      const generalCastLines: TimedExportLine[] = (
        linkedRaidPlan.timeline.general?.casts ?? []
      )
        .map((cast) => {
          const phaseIndex = getPhaseAt(cast.at, phaseBoundaries)
          const phaseStart = getPhaseStart(phaseIndex, phaseBoundaries)
          const relativeTime = Math.max(cast.at - phaseStart, 0)
          const parts = [
            `time:${formatTimeValue(relativeTime)}`,
            `ph:${phaseIndex + 1}`,
          ]
          const bossSpellId = getNearestBossSpellId(cast.at, bossEntries)
          if (bossSpellId) {
            parts.push(`bossSpell:${bossSpellId}`)
          }
          parts.push('tag:everyone')
          if (cast.spell_id !== null) {
            parts.push(`spellid:${cast.spell_id}`)
          }
          const shouldAddTargetsToReminderText =
            cast.add_targets_to_reminder_text !== false
          const castMessage = `${cast.message?.trim() ?? ''}${
            shouldAddTargetsToReminderText
              ? toCastTargetSuffix(
                  cast.id,
                  linkedPlanData.castTargetResolverResult.resolvedByCastId,
                )
              : ''
          }`.trim()
          appendTextProperty(parts, castMessage)
          appendColorsProperty(parts, cast.rgb)
          appendTtsProperty(parts, cast.tts_message)
          if (cast.glow_targets_on_raid_frames !== false) {
            appendGlowunitProperty(
              parts,
              toCastTargetCharacterNames(
                cast.id,
                linkedPlanData.castTargetResolverResult.resolvedByCastId,
              ),
            )
          }
          if (cast.spell_id === null && !castMessage.length) {
            return null
          }
          return {
            at: cast.at,
            line: `${parts.join(';')};`,
          }
        })
        .filter((entry): entry is TimedExportLine => entry !== null)
      const castLines: TimedExportLine[] = [
        ...actorCastLines,
        ...generalCastLines,
      ].sort((left, right) => {
        if (left.at !== right.at) return left.at - right.at
        return left.line.localeCompare(right.line)
      })

      const assignmentLines: TimedExportLine[] = []
      const summaryByAbility = new Map<string, AbilitySummary>()

      linkedPlanData.linkedPlanAssignments.forEach((normalizedEntry) => {
        const ability = normalizedEntry.ability
        const row = normalizedEntry.row
        const assignmentGroups = getEffectiveAssignmentGroups(
          row.assignmentGroups,
          ability.assignment_groups,
        )
        const abilityName = ability.title?.trim() || row.name
        const assignmentLabel = ability.message?.trim().length
          ? ability.message.trim()
          : `${getSoakPrefix(abilityName)}${abilityName}`
        const viewMode = getEffectiveAssignmentViewMode(ability.view_mode)
        const customGrouping = getEffectiveAbilityCustomGrouping(
          ability.spell_id,
          ability.custom_grouping,
        )
        const scopes = getAssignmentScopeIndexes(
          row.entries,
          viewMode,
          customGrouping,
        )
        const useBoxNoteSettings =
          viewMode !== 'all_together' || assignmentGroups > 1
        scopes.forEach((scope) => {
          const representativeCast = normalizeAssignmentCast(
            ability.casts?.[scope.castIndexes[0]],
            assignmentGroups,
          )
          for (let boxIndex = 0; boxIndex < assignmentGroups; boxIndex += 1) {
            const key = `${row.abilityKey}:${scope.id}:${boxIndex}`
            const resolvedEntities =
              linkedPlanData.resolverResult.resolvedByScopeBoxKey[key] ?? []
            const tokens = toAssignmentEntityTokens(resolvedEntities)
            if (!tokens.length) continue
            const box = normalizeAssignmentBox(
              representativeCast.boxes[boxIndex],
            )
            const boxMessage = useBoxNoteSettings ? box.message?.trim() : ''
            const boxTtsMessage = useBoxNoteSettings
              ? box.tts_message?.trim()
              : ''
            const lineText = boxMessage?.length ? boxMessage : assignmentLabel
            const lineTtsMessage = boxTtsMessage?.length
              ? boxTtsMessage
              : ability.tts_message
            scope.castGroups.forEach((castGroup) => {
              const firstCastIndex = castGroup[0]
              const firstEntry = row.entries[firstCastIndex]
              if (!firstEntry) return
              const castAt = firstEntry.time
              const phaseIndex = getPhaseAt(castAt, phaseBoundaries)
              const phaseStart = getPhaseStart(phaseIndex, phaseBoundaries)
              const relativeTime = Math.max(castAt - phaseStart, 0)
              const parts = [
                `time:${formatTimeValue(relativeTime)}`,
                `ph:${phaseIndex + 1}`,
                `tag:${tokens.join(' ')}`,
                `text:${lineText}`,
              ]
              appendColorsProperty(parts, ability.rgb)
              appendTtsProperty(parts, lineTtsMessage)
              assignmentLines.push({
                at: castAt,
                line: `${parts.join(';')};`,
              })
            })

            const abilitySummary = summaryByAbility.get(
              normalizedEntry.abilityKey,
            ) ?? {
              label: assignmentLabel,
              slotOrder: [],
              tokensBySlot: new Map<string, string[]>(),
            }
            const summarySlotKey = `${scope.id}:${boxIndex}`
            if (!abilitySummary.tokensBySlot.has(summarySlotKey)) {
              abilitySummary.slotOrder.push(summarySlotKey)
            }
            const existingTokens =
              abilitySummary.tokensBySlot.get(summarySlotKey) ?? []
            tokens.forEach((token) => {
              if (!existingTokens.includes(token)) {
                existingTokens.push(token)
              }
            })
            abilitySummary.tokensBySlot.set(summarySlotKey, existingTokens)
            summaryByAbility.set(normalizedEntry.abilityKey, abilitySummary)
          }
        })
      })

      assignmentLines.sort((left, right) => {
        if (left.at !== right.at) return left.at - right.at
        return left.line.localeCompare(right.line)
      })

      summaryLines = Array.from(summaryByAbility.entries()).flatMap(
        ([, abilitySummary], abilityIndex) => {
          const lines = [
            abilitySummary.label,
            ...abilitySummary.slotOrder.map((slotKey, slotIndex) => {
              const tokens = abilitySummary.tokensBySlot.get(slotKey) ?? []
              return `${getSoakPrefix(abilitySummary.label)}${
                slotIndex + 1
              }: ${tokens.join(', ')}`
            }),
          ]
          if (abilityIndex < summaryByAbility.size - 1) {
            lines.push('')
          }
          return lines
        },
      )
      timedLines = [
        ...castLines.map((entry) => entry.line),
        ...assignmentLines.map((entry) => entry.line),
      ]
    }
  }

  if (timedLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...timedLines)
  }
  if (summaryLines.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(...summaryLines)
  }

  const inviteLine = buildEventActivityInviteListLine({
    activity,
    allEntries,
    selectedParticipants,
  })
  if (inviteLine.length > 0) {
    if (noteChunks.length > 0) noteChunks.push('')
    noteChunks.push(inviteLine)
  }

  return noteChunks.join('\n')
}
